import { Avatar, Box, Button, Link, Skeleton, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { useProfileData } from "../../../hooks/useProfileData";
import MobilesImg from "../../../assets/icons/mobile-figure.svg";
import {
  AndroidIcon,
  AppleIcon,
  ChatSNIcon,
  DashboardIcon,
  EventsIcon,
  ExploreIcon,
  FollowersIcon,
  FollowingIcon,
  GoStarIcon,
  NetworkIcon,
  PaymentIcon,
  PostsIcon,
  ProfileViewsIcon,
  VerifiedIcon,
} from "../../../assets/icons/MUI/LayoutIcons";
import APP_ROUTES from "../../../routers/routeConstants";
import { getNetworkRoute } from "../../../routers/routeUtils";

import MenuItem from "./MenuItem";
import AnalyticItem from "./AnalyticItem";
import {
  sideNavStyles,
  sideNavBoxStyles,
  avatarStyles,
  nameTextStyles,
  aboutTextStyles,
  menuListStyles,
  analyticsSectionStyles,
  analyticsListStyles,
  gradientBoxStyles,
  titleTextStyles,
  descriptionTextStyles,
  linkBoxStyles,
  linkStyles,
} from "./SideNav.styles";

const SideNav = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { data: userData, isLoading } = useProfileData();
  const { fullName, firstTwoChr, about, media, profileViewers, posts, followersCount, followingCount, isVerified } =
    userData;

  return (
    <Box component="aside" sx={sideNavStyles}>
      <Box sx={sideNavBoxStyles}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "16px",
            justifyContent: "center",
            width: "100%",
            position: "relative",
          }}
        >
          {isVerified && (
            <VerifiedIcon
              sx={{
                position: "absolute",
                top: "72px",
                left: "50%",
                transform: "translateX(-50%)",
                zIndex: "10",
              }}
            />
          )}
          <Avatar sx={avatarStyles} src={isLoading ? undefined : media}>
            {isLoading ? <Skeleton variant="circular" animation="wave" /> : firstTwoChr}
          </Avatar>
          <Typography variant="body16SemiBold" sx={nameTextStyles}>
            {isLoading ? <Skeleton variant="text" width="60%" animation="wave" sx={{ margin: "0 auto" }} /> : fullName}
          </Typography>

          <Typography variant="body12Regular" sx={aboutTextStyles}>
            {isLoading ? (
              <>
                <Skeleton variant="text" width="100%" animation="wave" />
                <Skeleton variant="text" width="100%" animation="wave" />
                <Skeleton variant="text" width="100%" animation="wave" />
              </>
            ) : (
              about
            )}
          </Typography>
        </Box>
        <Button variant="outlined-sm" onClick={() => navigate(APP_ROUTES.ENTITY)} fullWidth>
          {t("layout.sideNav.viewProfile")}
        </Button>
        <Box sx={menuListStyles} component="ul">
          <MenuItem to={APP_ROUTES.DASHBOARD} label={t("layout.sideNav.dashboard")} icon={<DashboardIcon />} />
          <MenuItem to={APP_ROUTES.EXPLORE} label={t("layout.sideNav.explore")} icon={<ExploreIcon />} />
          {/* <MenuItem to={APP_ROUTES.PAYMENT} label={t("layout.sideNav.payment")} icon={<PaymentIcon />} /> */}
          <MenuItem to={APP_ROUTES.EVENTS} label={t("layout.sideNav.events")} icon={<EventsIcon />} />
          <MenuItem to={APP_ROUTES.NETWORK} label={t("layout.sideNav.network")} icon={<NetworkIcon />} />
          <MenuItem to={APP_ROUTES.GO_STAR} label={t("layout.sideNav.goStar")} icon={<GoStarIcon />} />
          <MenuItem to={APP_ROUTES.CHAT} label={t("layout.sideNav.chat")} icon={<ChatSNIcon />} />
        </Box>
        <Box sx={analyticsSectionStyles}>
          <Typography variant="body14Bold">{t("layout.sideNav.hotAnalytics")}</Typography>
          <Box component="ul" sx={analyticsListStyles}>
            <AnalyticItem
              icon={<ProfileViewsIcon />}
              label={t("layout.sideNav.profileViewers")}
              value={profileViewers}
              isLoading={isLoading}
            />
            <AnalyticItem icon={<PostsIcon />} label={t("layout.sideNav.posts")} value={posts} isLoading={isLoading} />
            <AnalyticItem
              icon={<FollowersIcon />}
              label={t("layout.sideNav.followers")}
              value={followersCount}
              isLoading={isLoading}
              link={getNetworkRoute("followers")}
            />
            <AnalyticItem
              icon={<FollowingIcon />}
              label={t("layout.sideNav.following")}
              value={followingCount}
              isLoading={isLoading}
              link={getNetworkRoute("following")}
            />
          </Box>
        </Box>
      </Box>
      <Box sx={gradientBoxStyles}>
        <Typography sx={titleTextStyles}>{t("layout.sideNav.oneApp")}</Typography>
        <Typography sx={descriptionTextStyles}>{t("layout.sideNav.connectWith")}</Typography>
        <Box sx={linkBoxStyles}>
          <Link
            sx={linkStyles}
            href="https://apps.apple.com/eg/app/goalngo/id6443919098"
            target="_blank"
            rel="noopener noreferrer"
          >
            <AppleIcon />
          </Link>
          <Link
            sx={linkStyles}
            href="https://play.google.com/store/apps/details?id=com.goalngo_tech.goalngo&hl=en&pli=1"
            target="_blank"
            rel="noopener noreferrer"
          >
            <AndroidIcon />
          </Link>
        </Box>
      </Box>
    </Box>
  );
};

export default SideNav;
